import React, { useState, useRef, useEffect } from "react";
import "./UploadStory.css";
import "../../Components/Header/Header.css";
import Header from "./../../Components/Header/Header";
import ConfirmIconLogo from "../../assets/images/ConfirmIconLogo.png";
import UploadStoryLeftIcon from "../../assets/images/UploadStoryLeftIcon.svg";
import UploadStoryRightIcon from "../../assets/images/UploadStoryRightIcon.svg";
import SuccessIcon from "../../assets/images/SuccessIcon.png";
import { callApi } from "../../utils/apiService.js";
import { videoCreatorEndPoint } from "../../Constants/constants.js";
import { useUser } from "../../Context/UserContext.jsx";
import Select from "react-select";
import { showToast } from "../../utils/toastUtils";

export default function UploadStory() {
  const [isUploading, setIsUploading] = useState(false);
  const [stories, setStories] = useState([
    {
      id: 1,
      file: null,
      preview: null,
      caption: "",
      type: "",
      language: "",
      contentCategory: "",
      uploadProgress: 0,
      uploadComplete: false,
      showRemoveButton: false,
    },
  ]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [showStoryViewer, setShowStoryViewer] = useState(false);
  const [showCategoryHelp, setShowCategoryHelp] = useState(false);
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
  const [selectedStoryIndex, setSelectedStoryIndex] = useState(0);
  const [storyTypes, setStoryTypes] = useState([]);
  const activeStory = stories[selectedStoryIndex] || stories[0];
  const storyErrors = validationErrors[activeStory?.id] || {};
  const addMoreFileRef = useRef(null);
  const uploadIntervals = useRef({});
  const fileInputRefs = useRef({});
  const { languages } = useUser();

  const updateValidationError = (storyId, field, message) => {
    setValidationErrors((prevErrors) => {
      const storyErrors = prevErrors[storyId] || {};

      if (!message) {
        const updatedStoryErrors = { ...storyErrors };
        delete updatedStoryErrors[field];

        if (Object.keys(updatedStoryErrors).length === 0) {
          const nextErrors = { ...prevErrors };
          delete nextErrors[storyId];
          return nextErrors;
        }

        return { ...prevErrors, [storyId]: updatedStoryErrors };
      }

      return {
        ...prevErrors,
        [storyId]: {
          ...storyErrors,
          [field]: message,
        },
      };
    });
  };

  const fetchStoryTypes = async () => {
    try {
      const response = await callApi(
        `${videoCreatorEndPoint}story/types/list`,
        "GET",
      );
      if (response?.status) {
        setStoryTypes(response?.data || []);
      }
    } catch (err) {}
  };

  useEffect(() => {
    fetchStoryTypes();
  }, []);

  useEffect(() => {
    if (!showCategoryHelp) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setShowCategoryHelp(false);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showCategoryHelp]);
  const simulateUploadProgress = (storyId) => {
    if (uploadIntervals.current[storyId]) {
      clearInterval(uploadIntervals.current[storyId]);
    }

    updateStory(storyId, {
      uploadProgress: 0,
      uploadComplete: false,
    });

    uploadIntervals.current[storyId] = setInterval(() => {
      setStories((prevStories) =>
        prevStories.map((story) => {
          if (story.id !== storyId) return story;

          const newProgress = Math.min(
            story.uploadProgress + Math.floor(Math.random() * 15) + 10,
            100,
          );

          if (newProgress >= 100) {
            clearInterval(uploadIntervals.current[storyId]);
            delete uploadIntervals.current[storyId];

            return {
              ...story,
              uploadProgress: 100,
              uploadComplete: true,
              showRemoveButton: true,
            };
          }

          return {
            ...story,
            uploadProgress: newProgress,
          };
        }),
      );
    }, 500);
  };
  const handleStoryDrop = (e, storyId) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files[0];
    if (
      file &&
      (file.type.startsWith("video/") || file.type.startsWith("image/"))
    ) {
      updateStory(storyId, {
        file: file,
        preview: URL.createObjectURL(file),
      });
      updateValidationError(storyId, "file", "");
      simulateUploadProgress(storyId);
    }
  };

  const handleStoryChange = (e, storyId) => {
    const file = e.target.files[0];

    if (
      file &&
      (file.type.startsWith("video/") || file.type.startsWith("image/"))
    ) {
      updateStory(storyId, {
        file,
        preview: URL.createObjectURL(file),
      });
      updateValidationError(storyId, "file", "");
      const index = stories.findIndex((story) => story.id === storyId);

      setSelectedStoryIndex(index);

      simulateUploadProgress(storyId);
    }
  };

  const updateStory = (storyId, updates) => {
    setStories((prevStories) =>
      prevStories.map((story) =>
        story.id === storyId ? { ...story, ...updates } : story,
      ),
    );
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleRemoveStory = (storyId) => {
    if (stories.length === 1) {
      alert("You must have at least one story");
      return;
    }
    setStories((prevStories) =>
      prevStories.filter((story) => story.id !== storyId),
    );
  };

  const handleAddMoreStoryFile = (e) => {
    const file = e.target.files[0];

    if (!file) {
      return;
    }

    const newId = Math.max(...stories.map((s) => s.id), 0) + 1;

    const newStory = {
      id: newId,
      file,
      preview: URL.createObjectURL(file),
      caption: "",
      type: "",
      language: "",
      contentCategory: "",
      uploadProgress: 0,
      uploadComplete: false,
      showRemoveButton: false,
    };

    setStories((prev) => {
      const updated = [...prev, newStory];

      setTimeout(() => {
        simulateUploadProgress(newId);
      }, 0);

      return updated;
    });
    setSelectedStoryIndex(stories.length);
    e.target.value = "";
  };

  const nextStory = () => {
    setSelectedStoryIndex((prev) =>
      prev === stories.length - 1 ? 0 : prev + 1,
    );
  };

  const prevStory = () => {
    setSelectedStoryIndex((prev) =>
      prev === 0 ? stories.length - 1 : prev - 1,
    );
  };
  const handlePublish = () => {
    const nextErrors = {};

    stories.forEach((story) => {
      const storyErrors = {};

      if (!story.file) {
        storyErrors.file = "Please upload a story file";
      }
      if (!story.type) {
        storyErrors.type = "Please select a story type";
      }
      if (!story.language) {
        storyErrors.language = "Please select a story language";
      }
      if (!story.contentCategory) {
        storyErrors.contentCategory = "Please select a content category";
      }
      if (!confirmChecked) {
        storyErrors.confirm =
          "Please confirm you have rights to use this content";
      }

      if (Object.keys(storyErrors).length > 0) {
        nextErrors[story.id] = storyErrors;
      }
    });

    setValidationErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setShowConfirmModal(true);
  };
  
  const uploadToS3 = async (uploadUrl, file) => {
    const response = await fetch(uploadUrl, {
      method: "PUT",
      headers: {
        "Content-Type": file.type,
      },
      body: file,
    });

    if (!response.ok) {
      throw new Error("Failed to upload file");
    }

    return true;
  };
  const createStory = async () => {
    try {
      const payload = {
        captions: stories.map((story) => story.caption || ""),
        storyTypeIds: stories.map((story) => story.type),
        storyLanguageIds: stories.map((story) => story.language),
        storyDurations: await Promise.all(
          stories.map(async (story) => {
            if (story.file?.type.startsWith("video/")) {
              return new Promise((resolve) => {
                const video = document.createElement("video");
                video.preload = "metadata";

                video.onloadedmetadata = () => {
                  resolve(Math.round(video.duration));
                };

                video.src = URL.createObjectURL(story.file);
              });
            }

            return 10; // image duration
          }),
        ),
        storyFileTypes: stories.map((story) => story.file?.type || ""),
        storySizes: stories.map((story) => story.file?.size || 0),
        storyNames: stories.map((story) =>
          story.file?.name
            ? story.file.name.substring(0, story.file.name.lastIndexOf(".")) ||
              story.file.name
            : "",
        ),
        contentCategories: stories.map((story) => story.contentCategory),
        contentRightsConfirmed: true,
      };

      const response = await callApi(
        `${videoCreatorEndPoint}story/create`,
        "POST",
        payload,
      );

      return response;
    } catch (error) {
      console.error("Create Story Error:", error);
      return null;
    }
  };

  const handleConfirmSubmit = async () => {
    setIsUploading(true);
    try {
      const response = await createStory();

      if (!response?.status) {
        setShowConfirmModal(false);
        showToast(
          "error",
          response?.message || response?.alert || "Failed to create story. Please try again.",
        );
        return;
      }

      const uploadData = response.data || [];

      // Validate count
      if (uploadData.length !== stories.length) {
        setShowConfirmModal(false);
        showToast("error", "Upload URL count mismatch");
        return;
      }

      // Upload all files in parallel
      await Promise.all(
        uploadData.map((item, index) =>
          uploadToS3(item.uploadUrl, stories[index].file),
        ),
      );

      if (response?.status) {
        setShowConfirmModal(false);
        setShowSuccessModal(true);
        setValidationErrors({});
        setConfirmChecked(false);

        setTimeout(() => {
          setStories([
            {
              id: 1,
              file: null,
              preview: null,
              caption: "",
              type: "",
              language: "",
              contentCategory: "",
              uploadProgress: 0,
              uploadComplete: false,
              showRemoveButton: false,
            },
          ]);

          setSelectedStoryIndex(0);
          setCurrentStoryIndex(0);
        }, 2000);
      } else {
        showToast("error", response?.message || "Failed to create story");
      }
    } catch (error) {
      setShowConfirmModal(false);
      showToast(
        "error",
        error?.response?.data?.message || error?.message || "Failed to create story. Please try again.",
      );
    } finally {
      setIsUploading(false);
    }
  };
  const handleCancel = () => {
    if (
      window.confirm(
        "Are you sure you want to cancel? All changes will be lost.",
      )
    ) {
      setValidationErrors({});
      setConfirmChecked(false);
      setStories([
        {
          id: 1,
          file: null,
          preview: null,
          caption: "",
          type: "",
          language: "",
          contentCategory: "",
          uploadProgress: 0,
          uploadComplete: false,
          showRemoveButton: false,
        },
      ]);
    }
  };

  const handleDone = () => {
    setShowSuccessModal(false);
    window.location.href = "/dashboard";
  };

  const handleViewApprovedList = () => {
    setShowSuccessModal(false);
    window.location.href = "/view-awaiting-list";
  };

  const storyTypeOptions = storyTypes.map((item) => ({
    value: item.id,
    label: item.dataName,
  }));

  const languageOptions = languages.map((item) => ({
    value: item.id,
    label: item.dataName,
  }));

  const contentCategoryOptions = [
    { value: "MOTION", label: "Motion" },
    { value: "STATIC", label: "Static" },
  ];

  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: "48px",
      borderRadius: "8px",
      borderColor: state.isFocused ? "#a855f7" : "#dbcfe8",
      backgroundColor: "#faf7fc",
      boxShadow: state.isFocused ? "0 0 0 3px rgba(168, 85, 247, 0.1)" : "none",
      "&:hover": {
        borderColor: "#a855f7",
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "0 12px",
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: "200px",
      overflowY: "auto",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#6b7280",
      fontSize: "14px",
    }),
    singleValue: (provided) => ({
      ...provided,
      fontSize: "14px",
      color: "#1f2937",
    }),
  };

  return (
    <div>
      {isUploading && (
        <div className="upload-loader-overlay">
          <div className="upload-loader" />
        </div>
      )}

      <div
        className={`story-page-wrapper ${
          showConfirmModal || showSuccessModal ? "story-page-blur" : ""
        }`}
      >
        <Header />
        <div className="upload-story-container">
          <div className="upload-story-content">
            <h1 className="story-page-title">Upload Story</h1>

            {/* Tabs */}
            <div className="story-tabs-container">
              <div className="story-tab story-tab-active">Story</div>
            </div>

            <form className="story-form">
              <div key={activeStory?.id} className="story-item-wrapper">
                <div className="story-form-grid">
                  {/* Left Column */}
                  <div className="story-form-left">
                    {/* Story File Section */}
                    <div className="story-form-section">
                      <label className="story-file-label">
                        Story Highlights File
                      </label>
                      <p className="story-file-description">
                        Upload your all video& Image file (PNG,JPEG ,MP4, MOV,
                        AVI supported)
                      </p>

                      {!activeStory?.preview ? (
                        <div
                          className={`story-upload-area ${storyErrors.file ? "story-upload-area-error" : ""}`}
                          onDrop={(e) => handleStoryDrop(e, activeStory?.id)}
                          onDragOver={handleDragOver}
                        >
                          <p className="story-upload-main-text">
                            Upload Video & Image
                          </p>
                          <p className="story-upload-hint">
                            Drag and drop to upload video and image files
                          </p>
                          <button
                            type="button"
                            className="story-browse-button"
                            onClick={() =>
                              fileInputRefs.current[activeStory?.id]?.click()
                            }
                          >
                            Browse
                          </button>
                          <input
                            ref={(el) =>
                              (fileInputRefs.current[activeStory?.id] = el)
                            }
                            type="file"
                            accept="video/*,image/*"
                            onChange={(e) =>
                              handleStoryChange(e, activeStory?.id)
                            }
                            style={{ display: "none" }}
                          />
                        </div>
                      ) : (
                        <div className="story-preview-container">
                          {activeStory?.preview && (
                            <>
                              {activeStory?.file.type.startsWith("image/") ? (
                                <img
                                  src={activeStory?.preview}
                                  alt="Story Preview"
                                  className="story-file-preview"
                                  onClick={() => {
                                    setCurrentStoryIndex(selectedStoryIndex);
                                    setShowStoryViewer(true);
                                  }}
                                />
                              ) : (
                                <video
                                  src={activeStory?.preview}
                                  className="story-file-preview"
                                  onClick={() => {
                                    setCurrentStoryIndex(selectedStoryIndex);
                                    setShowStoryViewer(true);
                                  }}
                                />
                              )}

                              {!activeStory?.uploadComplete && (
                                <div className="story-progress-container">
                                  <div className="story-progress-bar">
                                    <div
                                      className="story-progress-fill"
                                      style={{
                                        width: `${activeStory?.uploadProgress}%`,
                                      }}
                                    />
                                  </div>
                                  <div className="story-progress-percentage">
                                    {Math.round(activeStory?.uploadProgress)}%
                                  </div>
                                </div>
                              )}

                              {/* {story.showRemoveButton && (
                                    <div className="story-success-modal-buttons">
                                      <button
                                        type="button"
                                        className="story-remove-file-button"
                                        onClick={() => {
                                          updateStory(story.id, {
                                            file: null,
                                            preview: null,
                                            uploadProgress: 0,
                                            uploadComplete: false,
                                            showRemoveButton: false,
                                          });
                                        }}
                                      >
                                        Remove
                                      </button>
                                    </div>
                                  )} */}
                            </>
                          )}
                        </div>
                      )}
                      {storyErrors.file && (
                        <p className="story-error-message">
                          {storyErrors.file}
                        </p>
                      )}

                      {activeStory?.preview && (
                        <div className="story-action-container">
                          <button
                            type="button"
                            className="story-remove-file-button"
                            onClick={() => addMoreFileRef.current?.click()}
                          >
                            Add More Story
                          </button>
                        </div>
                      )}
                      <input
                        ref={addMoreFileRef}
                        type="file"
                        accept="video/*,image/*"
                        style={{ display: "none" }}
                        onChange={handleAddMoreStoryFile}
                      />

                      {/* {stories.length > 1 && stories[stories.length - 1].id !== story.id && (
                  <hr className="story-divider" />
                )} */}
                    </div>

                    {/* Story Type */}
                    <div className="story-form-section">
                      <label className="story-form-label">Story Type</label>
                      <Select
                        options={storyTypeOptions}
                        placeholder="Select a story type"
                        value={
                          storyTypeOptions.find(
                            (option) => option.value === activeStory?.type,
                          ) || null
                        }
                        onChange={(selected) => {
                          updateStory(activeStory?.id, {
                            type: selected?.value || "",
                          });
                          updateValidationError(activeStory?.id, "type", "");
                        }}
                        styles={{
                          ...customSelectStyles,
                          control: (provided, state) => ({
                            ...customSelectStyles.control(provided, state),
                            borderColor: storyErrors.type
                              ? "#ef4444"
                              : state.isFocused
                                ? "#a855f7"
                                : "#dbcfe8",
                          }),
                        }}
                      />
                      {storyErrors.type && (
                        <p className="story-error-message">
                          {storyErrors.type}
                        </p>
                      )}
                    </div>

                    {/* Story Language */}
                    <div className="story-form-section">
                      <label className="story-form-label">Story Language</label>
                      <Select
                        options={languageOptions}
                        placeholder="Select a story language"
                        value={
                          languageOptions.find(
                            (option) => option.value === activeStory?.language,
                          ) || null
                        }
                        onChange={(selected) => {
                          updateStory(activeStory?.id, {
                            language: selected?.value || "",
                          });
                          updateValidationError(
                            activeStory?.id,
                            "language",
                            "",
                          );
                        }}
                        styles={{
                          ...customSelectStyles,
                          control: (provided, state) => ({
                            ...customSelectStyles.control(provided, state),
                            borderColor: storyErrors.language
                              ? "#ef4444"
                              : state.isFocused
                                ? "#a855f7"
                                : "#dbcfe8",
                          }),
                        }}
                      />
                      {storyErrors.language && (
                        <p className="story-error-message">
                          {storyErrors.language}
                        </p>
                      )}
                    </div>

                    <div className="story-form-section">
                      <div className="story-category-label-row">
                        <label
                          className="story-form-label"
                          htmlFor={`story-content-category-${activeStory?.id}`}
                        >
                          Content Category
                        </label>
                        <button
                          type="button"
                          className="story-category-info-button"
                          aria-label="Learn about content categories"
                          onClick={() => setShowCategoryHelp(true)}
                        >
                          i
                        </button>
                      </div>
                      <Select
                        inputId={`story-content-category-${activeStory?.id}`}
                        options={contentCategoryOptions}
                        placeholder="Select Motion or Static"
                        value={
                          contentCategoryOptions.find(
                            (option) =>
                              option.value === activeStory?.contentCategory,
                          ) || null
                        }
                        onChange={(selected) => {
                          updateStory(activeStory?.id, {
                            contentCategory: selected?.value || "",
                          });
                          updateValidationError(
                            activeStory?.id,
                            "contentCategory",
                            "",
                          );
                        }}
                        styles={{
                          ...customSelectStyles,
                          control: (provided, state) => ({
                            ...customSelectStyles.control(provided, state),
                            borderColor: storyErrors.contentCategory
                              ? "#ef4444"
                              : state.isFocused
                                ? "#a855f7"
                                : "#dbcfe8",
                          }),
                        }}
                      />
                      {storyErrors.contentCategory && (
                        <p className="story-error-message">
                          {storyErrors.contentCategory}
                        </p>
                      )}
                    </div>

                    <div
                      className={`story-link-header ${storyErrors.confirm ? "story-checkbox-error" : ""}`}
                    >
                      <input
                        type="checkbox"
                        name="rememberMe"
                        className="checkbox-input"
                        checked={confirmChecked}
                        onChange={(e) => {
                          setConfirmChecked(e.target.checked);
                          if (e.target.checked) {
                            updateValidationError(
                              activeStory?.id,
                              "confirm",
                              "",
                            );
                          }
                        }}
                      />
                      <label
                        htmlFor="allow-comments"
                        className="story-checkbox-label"
                      >
                        I confirm this content is my own or I have rights to use
                        it.
                      </label>
                    </div>
                    {storyErrors.confirm && (
                      <p className="story-error-message">
                        {storyErrors.confirm}
                      </p>
                    )}
                  </div>

                  {/* Right Column */}
                  <div className="story-form-right">
                    <div className="story-form-section">
                      <label className="story-form-label">Story Caption</label>
                      <textarea
                        className={`${!activeStory?.preview ? "story-caption-textarea" : "story-success-upload"}`}
                        placeholder="Tell viewers about your reels"
                        value={activeStory?.caption}
                        onChange={(e) =>
                          updateStory(activeStory?.id, {
                            caption: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Form Buttons */}
              <div className="story-form-buttons">
                <button
                  type="button"
                  className="story-cancel-button"
                  onClick={handleCancel}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="story-publish-button"
                  onClick={handlePublish}
                >
                  Publish
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {showCategoryHelp && (
        <div
          className="story-modal-overlay story-category-help-overlay"
          onClick={() => setShowCategoryHelp(false)}
        >
          <div
            className="story-modal-content story-category-help-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="story-category-help-title"
            onClick={(event) => event.stopPropagation()}
          >
            <h2 id="story-category-help-title">
              Choosing a content category
            </h2>
            <p>
              <strong>Motion:</strong> Choose this when the story shows clear,
              meaningful movement or action, such as a car driving, someone
              walking, dancing, or exercising.
            </p>
            <p>
              <strong>Static:</strong> Choose this when the scene is mostly
              still. Small natural movements, such as head or hand gestures
              while someone speaks, are considered Static.
            </p>
            <button
              type="button"
              className="story-modal-submit-button"
              onClick={() => setShowCategoryHelp(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="story-modal-overlay">
          <div className="story-modal-content story-confirm-modal">
            <div className="story-success-modal-buttons">
              <img src={ConfirmIconLogo} alt="confirm icon" />
            </div>
            <h2 className="story-modal-title">Submit story for Approval?</h2>
            <p className="story-modal-message">
              Are you sure you want to submit this reel for admin approval? Once
              approved, you won't be able to edit or make changes to this story.
            </p>
            <div className="story-modal-buttons">
              <button
                className="story-modal-cancel-button"
                onClick={() => setShowConfirmModal(false)}
              >
                Cancel
              </button>
              <button
                className="story-modal-submit-button"
                onClick={handleConfirmSubmit}
                disabled={isUploading}
              >
                {isUploading ? "Submitting..." : "Submit"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="story-modal-overlay">
          <div className="story-modal-content story-success-modal">
            <div className="story-success-modal-buttons">
              <img src={SuccessIcon} alt="success icon" />
            </div>
            <h2 className="story-success-modal-title">
              Your Story has Been Submitted for Approval
            </h2>
            <p className="story-modal-message message-f-18">
              The story has been submitted successfully and is under admin
              review. You can check its status anytime in the Content Awaiting
              Approval list.
            </p>
            <div className="story-modal-buttons">
              <button className="story-modal-done-button" onClick={handleDone}>
                Okay
              </button>
              <button
                className="story-modal-submit-button"
                onClick={handleViewApprovedList}
              >
                View Approved list
              </button>
            </div>
          </div>
        </div>
      )}

      {showStoryViewer && (
        <div className="story-viewer-overlay">
          <img
            className={`story-nav-btn left ${
              currentStoryIndex === 0 ? "disabled" : ""
            }`}
            src={UploadStoryLeftIcon}
            alt="left-arrow"
            onClick={() => {
              if (currentStoryIndex > 0) {
                setCurrentStoryIndex(currentStoryIndex - 1);
              }
            }}
          />

          <div className="story-viewer-content">
            {stories[currentStoryIndex]?.file?.type.startsWith("image/") ? (
              <img
                src={stories[currentStoryIndex]?.preview}
                alt=""
                className="story-viewer-media"
              />
            ) : (
              <video
                src={stories[currentStoryIndex]?.preview}
                controls
                autoPlay
                className="story-viewer-media"
              />
            )}
          </div>
          <img
            className={`story-nav-btn right ${
              currentStoryIndex === stories.length - 1 ? "disabled" : ""
            }`}
            src={UploadStoryRightIcon}
            alt="right-arrow"
            onClick={() => {
              if (currentStoryIndex < stories.length - 1) {
                setCurrentStoryIndex(currentStoryIndex + 1);
              }
            }}
          />

          <button
            className="story-close-btn"
            onClick={() => setShowStoryViewer(false)}
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
}
